/**
 * Service worker registration and updates.
 *
 * Registered directly rather than through virtual:pwa-register, so the update
 * behaviour is explicit rather than depending on the plugin's mode.
 *
 * Two things have to be true at once:
 *
 *  1. A new worker must ACTIVATE ITSELF. A worker that only skips waiting when
 *     messaged strands any client whose code does not send that message - which
 *     is every client already installed at the time of the change. Verified:
 *     an old client reloaded, saw `waiting: true`, and stayed on the old build.
 *     `registerType: 'autoUpdate'` builds skipWaiting + clientsClaim into the
 *     worker, so it takes over on its own.
 *
 *  2. The RELOAD must be ours to time. The new worker taking control does not
 *     reload the page; that is done here, and it is held while a fitter is
 *     part-way through a sheet.
 *
 * Assets are content-hashed and index.html is served no-cache, so none of this
 * is about cache headers. It is about when a client notices a new build - a
 * worker only checks on a full page load, and an installed home-screen app
 * resumed from the background never performs one.
 */

const SW_URL = `${import.meta.env.BASE_URL}sw.js`.replace('//', '/');
const CHECK_INTERVAL = 10 * 60 * 1000;

/** Screens where a reload must wait. Marks autosave; the gesture does not. */
const HOLD_ON = [/\/inspections\/[^/]+\/run$/];

const inHeldScreen = () => HOLD_ON.some((re) => re.test(window.location.pathname));

export function startUpdates() {
  if (!('serviceWorker' in navigator)) return;

  let reloading = false;
  let pending = false;

  const applyReload = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  const release = () => {
    if (pending && !inHeldScreen()) {
      pending = false;
      applyReload();
    }
  };

  // The new worker has taken control: everything served from here is the new
  // build, so the page must be reloaded to match it.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // On the very first install there is nothing stale to replace.
    if (!sessionStorage.getItem('fc.sw.seen')) return;
    if (inHeldScreen()) pending = true;
    else applyReload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: import.meta.env.BASE_URL })
      .then((registration) => {
        if (navigator.serviceWorker.controller) {
          try {
            sessionStorage.setItem('fc.sw.seen', '1');
          } catch {
            /* private browsing */
          }
        }

        const check = () => registration.update().catch(() => {});

        // A resumed app is the case the browser's own logic misses entirely.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
        window.addEventListener('online', check);
        window.setInterval(check, CHECK_INTERVAL);
        check();
      })
      .catch(() => {
        /* no worker: the app still works, it just will not update itself */
      });
  });

  window.addEventListener('popstate', release);
  // pushState fires no event, so in-app navigation needs wrapping to be seen.
  const push = history.pushState.bind(history);
  history.pushState = (...args: Parameters<typeof history.pushState>) => {
    push(...args);
    queueMicrotask(release);
  };
}
